"use client";

/**
 * useSidebarStatsQuery - TanStack Query hook for sidebar stats
 *
 * Centralizes /api/users/sidebar-stats. Uses staleTime to prevent
 * re-fetching on every navigation (was firing on every pathname change).
 */

import { useQuery, type QueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { authFetch } from "@/lib/authFetch";

export const SIDEBAR_STATS_QUERY_KEY = ["users", "sidebar-stats"] as const;

/**
 * Invalidate the sidebar stats query so badge counts (e.g. the "Copy"
 * badge = active copy-enabled follows) refresh immediately after a
 * mutation, instead of lagging up to the 15s staleTime / 60s refetch.
 * Call after any action that changes copy_enabled: follow, unfollow,
 * pause, resume. Fire-and-forget — never block the UI on it.
 */
export function invalidateSidebarStats(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: SIDEBAR_STATS_QUERY_KEY }).catch(() => {});
}

export interface SidebarStats {
  notifications: number;
  feedbackReplies: number;
  activeReferrals: number;
  copytradingCount: number;
  newActivityCount: number;
}

export function useSidebarStatsQuery() {
  const { isLoggedIn } = useAuth();

  return useQuery<SidebarStats>({
    queryKey: SIDEBAR_STATS_QUERY_KEY,
    queryFn: async () => {
      const lastVisit = typeof window !== "undefined"
        ? localStorage.getItem("frenflow_last_activity_visit") || "0"
        : "0";
      const res = await authFetch(`/api/users/sidebar-stats?lastActivityVisit=${lastVisit}`);
      return res.json();
    },
    enabled: isLoggedIn,
    staleTime: 15_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false, // Pause polling when tab is hidden
  });
}
